import { ErrorType, StripeError } from './types';

export function isStripeError(obj: unknown): obj is StripeError {
  return (
    typeof obj === 'object' && obj !== null && 'type' in obj && 'message' in obj && 'code' in obj
  );
}

export function getErrorObject(error: ErrorType): Error {
  if (typeof error === 'string') {
    return new Error(error);
  }

  if (error instanceof Error) {
    return error;
  }

  // Special handling for Stripe SDK errors
  // they are thrown like errors, but are not
  // instances of the Error prototype
  if (isStripeError(error)) {
    return new Error(`${error.type || ''}: ${error.code || ''} - ${error.message || ''}`);
  }

  if ('id' in error && error.id) {
    return new Error(error.id);
  }

  return new Error(`Non-standard error object: ${JSON.stringify(error)}`);
}

export const isParamsEmpty = (params: { [key: string]: string | null | undefined }): boolean => {
  const keys = Object.keys(params);
  if (!keys.length) return true;
  if (keys.length > 1) return false;
  return params['0'] === ''; // Consider params empty when it looks like this: { "0": "" }
};

/** Codes axios gives a request that never got a response for reasons outside our code:
 * the network failed or the page went away (`ERR_NETWORK`), it timed out
 * (`ECONNABORTED`, or `ETIMEDOUT` with `clarifyTimeoutError`), or it was cancelled. */
const ROUTINE_NETWORK_ERROR_CODES: readonly string[] = [
  'ERR_NETWORK',
  'ECONNABORTED',
  'ETIMEDOUT',
  'ERR_CANCELED',
];

/**
 * An axios failure with no response and nothing to act on. Codes for our own bugs, such
 * as `ERR_BAD_OPTION` or `ERR_INVALID_URL`, also carry no response and are excluded.
 *
 * The legacy duck path delivers these as the raw `AxiosError`, because
 * `DefaultAPIErrorHandler` only builds an `APIError` when a response exists.
 * Duck-typed on the flag `axios.isAxiosError` checks, so ErrorReporting stays free of
 * an axios import.
 */
export const isRoutineAxiosNetworkError = (error: unknown): boolean => {
  if (typeof error !== 'object' || error === null) return false;
  const { isAxiosError, response, code } = error as {
    isAxiosError?: unknown;
    response?: unknown;
    code?: unknown;
  };
  return (
    isAxiosError === true &&
    response == null &&
    typeof code === 'string' &&
    ROUTINE_NETWORK_ERROR_CODES.includes(code)
  );
};
